import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../lib/cn.js";

const cardStyles = cva("rounded-lg border", {
  variants: {
    variant: {
      default: "bg-elevated border-line",
      elevated: "bg-elevated border-line shadow",
      inset: "bg-inset border-line",
    },
  },
  defaultVariants: { variant: "default" },
});

type CardProps = HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardStyles>;

const CardRoot = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, variant, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(cardStyles({ variant }), className)}
      {...rest}
    />
  );
});

const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-start justify-between gap-3 px-4 py-4 border-b border-line",
          className,
        )}
        {...rest}
      />
    );
  },
);

const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardBody({ className, ...rest }, ref) {
    return (
      <div ref={ref} className={cn("px-4 py-4", className)} {...rest} />
    );
  },
);

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-2 px-4 py-3 border-t border-line",
          className,
        )}
        {...rest}
      />
    );
  },
);

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
});

export type { CardProps };
